import { ThemeEvents } from '@theme/events';

const THRESHOLD_SELECTOR = '[data-cart-threshold]';
const AMOUNT_TOKEN = /{{\s*amount\s*}}/gi;
const formatterCache = new Map();

let cartRequestPromise = null;

const getCartEndpoint = () => {
  const root = window.Shopify?.routes?.root ?? '/';
  return `${root.replace(/\/$/, '')}/cart.js`;
};

const getThresholdElements = () => Array.from(document.querySelectorAll(THRESHOLD_SELECTOR));

const parseNumber = (value) => {
  if (value == null || value === '') return 0;
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const getFormatter = (currency, zeroDecimals = false) => {
  const cacheKey = `${currency || 'default'}::${zeroDecimals ? 'int' : 'default'}`;
  if (!formatterCache.has(cacheKey)) {
    const locale = document.documentElement.lang || undefined;
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency || 'USD',
      ...(zeroDecimals
        ? {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }
        : {}),
    });
    formatterCache.set(cacheKey, formatter);
  }
  return formatterCache.get(cacheKey);
};

const formatMoney = (cents, currency) => {
  const zeroDecimals = Math.abs(cents) % 100 === 0;
  const formatter = getFormatter(currency, zeroDecimals);
  return formatter.format(cents / 100);
};

const hasAmountToken = (text) => {
  if (!text) return false;
  AMOUNT_TOKEN.lastIndex = 0;
  return AMOUNT_TOKEN.test(text);
};

const buildMessage = (element, remainingCents, currency) => {
  const template = element.dataset.messageTemplate?.trim() || 'You are {{ amount }} away from free shipping';
  const success = element.dataset.successMessage?.trim() || "You've unlocked free shipping!";
  const formattedAmount = formatMoney(remainingCents, currency);

  if (remainingCents > 0) {
    if (hasAmountToken(template)) {
      AMOUNT_TOKEN.lastIndex = 0;
      return template.replace(AMOUNT_TOKEN, formattedAmount).trim();
    }
    return `${template} ${formattedAmount}`.trim();
  }

  if (hasAmountToken(success)) {
    AMOUNT_TOKEN.lastIndex = 0;
    return success.replace(AMOUNT_TOKEN, formattedAmount).trim();
  }

  return success;
};

const updateElement = (element, cartTotalCents) => {
  const thresholdLimit = parseNumber(element.dataset.thresholdLimit);
  if (thresholdLimit <= 0) {
    return;
  }

  const total = typeof cartTotalCents === 'number' ? cartTotalCents : parseNumber(element.dataset.cartTotal);
  element.dataset.cartTotal = total;

  const remaining = Math.max(thresholdLimit - total, 0);
  const progress = thresholdLimit ? Math.min(total / thresholdLimit, 1) : 0;
  const currency = element.dataset.currencyCode || undefined;

  const message = buildMessage(element, remaining, currency);

  const progressEl = element.querySelector('[data-threshold-progress]');
  if (progressEl) {
    progressEl.style.width = `${(progress * 100).toFixed(2)}%`;
  }

  const barEl = element.querySelector('.cart-threshold__bar');
  if (barEl) {
    barEl.setAttribute('aria-valuenow', total.toString());
    barEl.setAttribute('aria-valuetext', message);
  }

  const messageEl = element.querySelector('[data-threshold-message]');
  if (messageEl) {
    messageEl.textContent = message;
  }
};

const updateAllThresholds = (cartData) => {
  const elements = getThresholdElements();
  if (!elements.length) return;

  const total = cartData?.total_price;
  elements.forEach((element) => updateElement(element, total));
};

const fetchCart = () => {
  if (!cartRequestPromise) {
    cartRequestPromise = fetch(getCartEndpoint(), {
      credentials: 'same-origin',
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart fetch failed with status ${response.status}`);
        return response.json();
      })
      .catch((error) => {
        console.error('[cart-threshold] Failed to refresh cart data', error);
        return null;
      })
      .finally(() => {
        cartRequestPromise = null;
      });
  }

  return cartRequestPromise;
};

const refreshFromCart = () => {
  if (!getThresholdElements().length) return;
  fetchCart().then((cartData) => {
    if (!cartData) return;
    updateAllThresholds(cartData);
  });
};

const init = () => {
  updateAllThresholds();
  document.addEventListener(ThemeEvents.cartUpdate, () => {
    refreshFromCart();
    
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
